import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Upload, Sliders, BarChart3, TrendingUp, ShieldCheck, CheckCircle2, AlertTriangle, Timer, ShoppingCart, DollarSign, LineChart, Users } from "lucide-react";
import MarketingLayout from "@/components/MarketingLayout";
import Sparkline from "@/components/Sparkline";
import { api } from "@/lib/api";

const OPPS = [
  { label: "Reorder Opportunities", value: "12 SKUs", icon: ShoppingCart, tone: "forest" },
  { label: "Stockout Risk", value: "5 SKUs", icon: AlertTriangle, tone: "gold" },
  { label: "Slow Inventory", value: "18 SKUs", icon: Timer, tone: "slate" },
  { label: "Pricing Opportunities", value: "9 SKUs", icon: DollarSign, tone: "forest" },
  { label: "Market Comparison", value: "7 comparables", icon: LineChart, tone: "slate" },
];

const STEPS = [
  { n: "01", title: "Upload inventory", body: "Drop in your inventory CSV or XLSX export. No re-keying required." },
  { n: "02", title: "Upload sales", body: "Add 60–90 days of POS/sales export data — the file you already generate." },
  { n: "03", title: "Confirm columns", body: "TreadMargin suggests common field mappings. You confirm or override." },
  { n: "04", title: "Analyze", body: "We calculate velocity, days of supply, stockout risk, slow movers, and margins." },
  { n: "05", title: "Act", body: "Get prioritized recommendations you can accept, dismiss, or export." },
];

const CAPS = [
  { title: "Reorder engine", body: "Days of supply, stockout timing and reorder quantities per SKU — grounded in your own sales velocity." },
  { title: "Slow inventory", body: "See exactly which SKUs are tying up capital, how much, and for how long." },
  { title: "Pricing intelligence", body: "Gross margin dollars, margin %, and pricing signals — SKU by SKU." },
  { title: "Market comparison", body: "When authorized market data is available, compare your prices against a real observed range." },
  { title: "Recommendations", body: "Every suggestion shows the why, the evidence and a confidence level. No black box." },
  { title: "Ask TreadMargin", body: "Ask questions in plain English. Answers are grounded in your uploaded data — never fabricated." },
];

const FAQ = [
  { q: "Is TreadMargin a POS or shop-management system?", a: "No. TreadMargin sits on top of the systems you already use. You keep your POS. We turn your exports into decisions." },
  { q: "What files do I need to upload?", a: "An inventory export and a recent sales/POS export (CSV or XLSX). We support flexible column names and let you confirm mappings." },
  { q: "Where does market pricing come from?", a: "Only from authorized data sources. If a real comparable is not available for a SKU, we say so — we never fabricate competitor prices." },
  { q: "Is my data secure?", a: "Each dealership's data is fully isolated. We treat uploaded inventory and sales data as confidential business information." },
  { q: "Do you replace my purchasing decisions?", a: "No. TreadMargin recommends. You decide. We never modify prices or place orders on your behalf." },
];

function toneClasses(tone) {
  if (tone === "forest") return { bg: "bg-[#14624A]", text: "text-white", chip: "bg-[#14624A]/10 text-[#14624A]" };
  if (tone === "gold") return { bg: "bg-[#C9943D]", text: "text-[#0F1115]", chip: "bg-[#C9943D]/15 text-[#8a6318]" };
  return { bg: "bg-[#0F1115]", text: "text-white", chip: "bg-[#3B424B]/10 text-[#3B424B]" };
}

export default function Landing() {
  const [impact, setImpact] = useState(null);
  const [network, setNetwork] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [billing, setBilling] = useState("monthly");

  useEffect(() => {
    api.get("/public/impact").then((r) => setImpact(r.data)).catch(() => {});
    api.get("/public/network").then((r) => setNetwork(r.data)).catch(() => {});
  }, []);

  const PRICES = {
    solo:     { monthly: { amt: 49,  key: "solo_shop_monthly"       }, yearly: { amt: 490,  key: "solo_shop_yearly"       } },
    founding: { monthly: { amt: 99,  key: "founding_dealer_monthly" }, yearly: { amt: 990,  key: "founding_dealer_yearly" } },
    multi:    { monthly: { amt: 249, key: "multi_location_monthly"  }, yearly: { amt: 2490, key: "multi_location_yearly"  } },
  };
  const priceOf = (tier) => PRICES[tier][billing];
  const perLabel = billing === "monthly" ? "CAD / month" : "CAD / year";

  const startCheckout = async (tier) => {
    const lookupKey = priceOf(tier).key;
    if (!localStorage.getItem("tm_token")) {
      window.location.href = `/signup?next=/%23pricing`;
      return;
    }
    setCheckoutBusy(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookupKey, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
    } finally { setCheckoutBusy(false); }
  };

  return (
    <MarketingLayout>
      {/* Hero */}
      <section className="tm-container pt-14 sm:pt-20 pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="tm-demo-tag mb-6" data-testid="hero-early-tag">Early Access · Independent tire dealers · Canada</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight" data-testid="hero-heading">
              Your Monday-morning buying list, from data you already have.
            </h1>
            <p className="mt-6 text-[17px] text-[#3B424B] leading-relaxed max-w-xl" data-testid="hero-sub">
              Upload your POS export once. TreadMargin tells you what to reorder before it stocks out, which slow-movers are tying up capital, and where you're bleeding margin on tires that now cost more than you're selling for. No POS switch. No auto-pricing. Cancel anytime.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/early-access" className="tm-btn-gold" data-testid="hero-cta-early">Get Early Access <ArrowUpRight size={16} /></Link>
              <a
                href="/demo"
                className="tm-btn-outline"
                data-testid="hero-cta-demo"
              >Book a 15-min demo</a>
              <a href="#how" className="tm-btn-outline" data-testid="hero-cta-how">See how it works</a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <div><div className="text-2xl font-bold">$5k+</div><div className="text-xs text-[#3B424B] mt-1">Typical dead inventory freed / quarter</div></div>
              <div><div className="text-2xl font-bold">2-4</div><div className="text-xs text-[#3B424B] mt-1">Preventable stockouts flagged / month</div></div>
              <div><div className="text-2xl font-bold">20 min</div><div className="text-xs text-[#3B424B] mt-1">From CSV upload to first buying list</div></div>
            </div>
          </div>          {/* Analytics preview */}
          <div className="lg:col-span-6" data-testid="hero-preview">
            <div className="tm-card p-6 sm:p-7 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">This Week's Opportunities</div>
                  <div className="text-lg font-bold mt-1">Dealership Preview</div>
                </div>
                <div className="tm-demo-tag">Illustrative Demo Data</div>
              </div>
              <div className="grid gap-3">
                {OPPS.map((o) => {
                  const t = toneClasses(o.tone);
                  return (
                    <div key={o.label} className="flex items-center justify-between border border-[var(--tm-border)] rounded-lg px-4 py-3 bg-white">
                      <div className="flex items-center gap-3">
                        <div className={`h-9 w-9 rounded-lg ${t.bg} ${t.text} flex items-center justify-center`}><o.icon size={16} /></div>
                        <div>
                          <div className="text-sm font-semibold">{o.label}</div>
                          <div className="text-xs text-[#3B424B]">Grounded in your uploaded data</div>
                        </div>
                      </div>
                      <div className="text-sm font-mono font-semibold">{o.value}</div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 text-xs text-[#3B424B] flex items-center gap-2"><ShieldCheck size={13} /> Numbers shown are illustrative — not a real dealer's results.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact / dealer success wall — hidden by default at pre-launch.  Flip
          REACT_APP_LANDING_SHOW_IMPACT=true in frontend/.env once you have real
          dealer momentum to show off. */}
      {process.env.REACT_APP_LANDING_SHOW_IMPACT === "true" && impact && (
      <section aria-labelledby="impact-heading" className="border-y border-[var(--tm-border)] bg-white" data-testid="impact-section">
        <div className="tm-container py-14">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold mb-2">Signals so far</div>
              <h2 id="impact-heading" className="text-2xl sm:text-3xl font-bold">Aggregated activity across the platform.</h2>
              <p className="mt-2 text-sm text-[#3B424B] max-w-2xl">Counts are anonymised and cover every organisation using TreadMargin. We never publish individual dealer numbers.</p>
            </div>
            <div className="text-xs text-[#3B424B]" data-testid="impact-asof">{impact?.as_of ? `As of ${new Date(impact.as_of).toLocaleString()}` : ""}</div>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { key: "dealers_on_platform", label: "Dealers on platform", history: "dealer_history" },
              { key: "waitlist_count", label: "On the early-access list", history: null },
              { key: "skus_analyzed", label: "SKUs analysed", history: null },
              { key: "reorder_signals_generated", label: "Reorder signals flagged", history: "reorder_history" },
              { key: "sales_rows_analyzed", label: "Sales rows analysed", history: null },
            ].map((s) => (
              <div key={s.key} className="tm-card p-5 bg-white" data-testid={`impact-${s.key}`}>
                <div className="text-[11px] uppercase tracking-wider text-[#3B424B] font-semibold">{s.label}</div>
                <div className="mt-2 flex items-end justify-between gap-2">
                  <div className="text-3xl font-bold font-mono text-[#0F1115]">{impact ? (impact[s.key] ?? 0).toLocaleString() : "—"}</div>
                  {s.history && impact?.[s.history]?.length ? (
                    <div data-testid={`impact-spark-${s.key}`}><Sparkline data={impact[s.history]} width={80} height={26} /></div>
                  ) : null}
                </div>
                {s.history && impact?.[s.history]?.length ? (
                  <div className="mt-1 text-[10px] text-[#3B424B]">Last 6 months</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Dealer Network — Wall of Fame */}
      {network && network.contributing_dealers > 0 && (
        <section className="bg-[var(--tm-off)] border-y border-[var(--tm-border)]" data-testid="dealer-network-section">
          <div className="tm-container py-16">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold mb-3 flex items-center gap-1.5"><Users size={13} /> Dealer network</div>
              <h2 className="text-3xl sm:text-4xl font-bold">
                <span data-testid="network-dealer-count">{network.contributing_dealers}</span> {network.contributing_dealers === 1 ? "shop is" : "shops are"} contributing regional pricing intelligence.
              </h2>
              <p className="mt-4 text-[#3B424B] max-w-xl">
                Every subscribing dealer can anonymously pool their selling prices to see the regional median across other opted-in shops. Individual dealers are <b>never named</b>. Data only becomes visible for a (province, brand, size) group once 3+ dealers have contributed — that's how we keep every observation trustworthy and every dealer's numbers private.
              </p>
              <div className="mt-6 flex flex-wrap gap-6 text-sm text-[#3B424B]">
                <div><span className="font-mono font-bold text-[#0F1115] text-2xl" data-testid="network-provinces">{network.provinces.length}</span> province{network.provinces.length === 1 ? "" : "s"} covered</div>
                <div><span className="font-mono font-bold text-[#0F1115] text-2xl" data-testid="network-groups">{network.groups_above_k3}</span> SKU groups above k=3 anonymity threshold</div>
              </div>
              {network.provinces.length > 0 && (
                <div className="mt-4 text-xs text-[#3B424B]">Regions: {network.provinces.join(" · ")}</div>
              )}
            </div>
          </div>
        </section>
      )}


      {/* How it works */}
      <section id="how" className="bg-[var(--tm-off)] border-y border-[var(--tm-border)]">
        <div className="tm-container py-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold mb-3">How It Works</div>
            <h2 className="text-3xl sm:text-4xl font-bold">Five steps from export to action.</h2>
            <p className="mt-4 text-[#3B424B]">No POS replacement required. Start with the files you already export.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mt-12">
            {STEPS.map((s) => (
              <div key={s.n} className="tm-card p-5 bg-white" data-testid={`step-${s.n}`}>
                <div className="text-[11px] font-mono font-semibold text-[#C9943D]">{s.n}</div>
                <div className="mt-2 font-semibold">{s.title}</div>
                <div className="mt-2 text-sm text-[#3B424B] leading-relaxed">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section id="capabilities" className="tm-container py-20">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold mb-3">Capabilities</div>
          <h2 className="text-3xl sm:text-4xl font-bold">An intelligence layer, not another system to run.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          {CAPS.map((c) => (
            <div key={c.title} className="tm-card p-6" data-testid={`cap-${c.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-semibold">{c.title}</div>
              <div className="mt-2 text-sm text-[#3B424B] leading-relaxed">{c.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-[var(--tm-off)] border-y border-[var(--tm-border)]">
        <div className="tm-container py-20">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div className="max-w-2xl">
              <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold mb-3">Pricing</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0F1115]" data-testid="pricing-heading">Priced for independent dealers. Grows with your shop.</h2>
              <p className="mt-5 text-[17px] text-[#0F1115] leading-relaxed" data-testid="pricing-lede">
                Three plans, all with the same core intelligence. Pick the one that fits your bay count today; upgrade the moment a bigger tier pays for itself.
              </p>
            </div>
            {/* Monthly / Yearly toggle */}
            <div className="inline-flex items-center gap-1 tm-card p-1 bg-white" role="tablist" aria-label="Billing period" data-testid="pricing-billing-toggle">
              <button
                type="button"
                onClick={() => setBilling("monthly")}
                className={`px-4 h-9 rounded-md text-sm font-medium transition-colors ${billing === "monthly" ? "bg-[var(--tm-charcoal)] text-white" : "text-[#0F1115] hover:bg-[var(--tm-off)]"}`}
                aria-pressed={billing === "monthly"}
                data-testid="pricing-toggle-monthly"
              >Monthly</button>
              <button
                type="button"
                onClick={() => setBilling("yearly")}
                className={`px-4 h-9 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-2 ${billing === "yearly" ? "bg-[var(--tm-charcoal)] text-white" : "text-[#0F1115] hover:bg-[var(--tm-off)]"}`}
                aria-pressed={billing === "yearly"}
                data-testid="pricing-toggle-yearly"
              >
                Yearly
                <span className="tm-badge bg-[var(--tm-gold)]/20 text-[#8a6318] text-[10px] leading-none">Save 2 months</span>
              </button>
            </div>
          </div>

          <div className="mt-12 grid gap-6 lg:grid-cols-3 items-stretch">

            {/* ---------- Solo Shop ---------- */}
            <div className="bg-white text-[#0F1115] rounded-xl p-7 border border-[var(--tm-border)] shadow-sm flex flex-col" data-testid="pricing-card-solo">
              <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Solo Shop</div>
              <div className="mt-3 text-3xl font-bold" data-testid="pricing-solo-amount">${priceOf("solo").amt} <span className="text-base font-medium text-[#3B424B]">{perLabel}</span></div>
              {billing === "yearly" && <div className="text-xs text-[var(--tm-forest)] font-semibold mt-1">Two months free vs. monthly</div>}
              <div className="mt-2 text-sm text-[#3B424B] leading-relaxed">For a single-location dealer just starting to run tighter numbers.</div>
              <ul className="mt-6 space-y-2.5 text-[14px] text-[#0F1115] flex-1">
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> 1 location, up to 500 active SKUs</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Inventory + POS upload &amp; column mapping</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Reorder &amp; stockout intelligence</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Slow-inventory report</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Weekly email digest</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Ask TreadMargin (10 questions / day)</li>
              </ul>
              <div className="mt-6 pt-5 border-t border-[var(--tm-border)] space-y-2">
                <button onClick={() => startCheckout("solo")} disabled={!!checkoutBusy} className="tm-btn-outline w-full" data-testid="pricing-solo-cta">
                  {checkoutBusy === priceOf("solo").key ? "Starting checkout…" : `Start Solo — $${priceOf("solo").amt}/${billing === "monthly" ? "mo" : "yr"}`}
                </button>
                <Link to="/early-access?plan=solo" className="block text-center text-xs text-[#3B424B] hover:text-[#0F1115] underline underline-offset-2" data-testid="pricing-solo-list">Or just add me to the list</Link>
              </div>
            </div>

            {/* ---------- Founding Dealer (most popular) ---------- */}
            <div className="bg-white text-[#0F1115] rounded-xl p-7 border-2 border-[var(--tm-forest)] shadow-lg flex flex-col relative lg:-my-2" data-testid="pricing-card-founding">
              <div className="absolute -top-3 left-6 tm-badge bg-[#14624A] text-white shadow-sm">Most popular · Locked founding rate</div>
              <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Founding Dealer</div>
              <div className="mt-3 text-3xl font-bold" data-testid="pricing-founding-amount">${priceOf("founding").amt} <span className="text-base font-medium text-[#3B424B]">{perLabel}</span></div>
              {billing === "yearly" && <div className="text-xs text-[var(--tm-forest)] font-semibold mt-1">Two months free vs. monthly</div>}
              <div className="mt-2 text-sm text-[#3B424B] leading-relaxed">The full pricing brain for a growing independent shop. Locked while the cohort is open.</div>
              <ul className="mt-6 space-y-2.5 text-[14px] text-[#0F1115] flex-1">
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Up to 3 locations, unlimited SKUs</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Everything in Solo</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Pricing intelligence (margin $ &amp; %)</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Market comparison from your authorized feeds</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Recommendations with evidence &amp; confidence</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Ask TreadMargin — <b>unlimited</b> + PDF export</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Priority email support</li>
              </ul>
              <div className="mt-6 pt-5 border-t border-[var(--tm-border)] space-y-2">
                <button onClick={() => startCheckout("founding")} disabled={!!checkoutBusy} className="tm-btn-primary w-full" data-testid="pricing-precommit">
                  {checkoutBusy === priceOf("founding").key ? "Starting checkout…" : `Pre-commit — $${priceOf("founding").amt}/${billing === "monthly" ? "mo" : "yr"}`}
                </button>
                <Link to="/early-access?plan=founding" className="block text-center text-xs text-[#3B424B] hover:text-[#0F1115] underline underline-offset-2" data-testid="pricing-card-cta">Or just add me to the list</Link>
              </div>
            </div>

            {/* ---------- Multi-Location Pro ---------- */}
            <div className="bg-white text-[#0F1115] rounded-xl p-7 border border-[var(--tm-border)] shadow-sm flex flex-col" data-testid="pricing-card-multi">
              <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold">Multi-Location Pro</div>
              <div className="mt-3 text-3xl font-bold" data-testid="pricing-multi-amount">${priceOf("multi").amt} <span className="text-base font-medium text-[#3B424B]">{perLabel}</span></div>
              {billing === "yearly" && <div className="text-xs text-[var(--tm-forest)] font-semibold mt-1">Two months free vs. monthly</div>}
              <div className="mt-2 text-sm text-[#3B424B] leading-relaxed">For chains, franchise groups and multi-bay operators who need every location on the same numbers.</div>
              <ul className="mt-6 space-y-2.5 text-[14px] text-[#0F1115] flex-1">
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Unlimited locations &amp; user seats</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Everything in Founding Dealer</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Per-location dashboards &amp; digests</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> SMS stockout alerts (Twilio)</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Monthly buying-strategy call</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Dedicated onboarding &amp; data migration</li>
                <li className="flex items-start gap-2"><CheckCircle2 size={16} className="text-[#14624A] shrink-0 mt-0.5" /> Priority email + phone support</li>
              </ul>
              <div className="mt-6 pt-5 border-t border-[var(--tm-border)] space-y-2">
                <button onClick={() => startCheckout("multi")} disabled={!!checkoutBusy} className="tm-btn-outline w-full" data-testid="pricing-multi-cta">
                  {checkoutBusy === priceOf("multi").key ? "Starting checkout…" : `Start Multi-Location — $${priceOf("multi").amt}/${billing === "monthly" ? "mo" : "yr"}`}
                </button>
                <Link to="/early-access?plan=multi" className="block text-center text-xs text-[#3B424B] hover:text-[#0F1115] underline underline-offset-2" data-testid="pricing-multi-list">Talk to us instead</Link>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-xs text-[#3B424B]">
            All plans include: multi-tenant data isolation · cancel anytime from your dashboard · your uploaded data stays yours.
          </div>

          {/* Compare plans table */}
          <div className="mt-16" data-testid="pricing-compare-wrap">
            <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold mb-3">Compare plans</div>
            <h3 className="text-2xl font-bold text-[#0F1115]">Every capability, side by side.</h3>
            <div className="mt-6 tm-card bg-white overflow-x-auto tm-scroll">
              <table className="w-full text-sm min-w-[720px]" data-testid="pricing-compare-table">
                <thead className="bg-[var(--tm-off)] text-left text-xs uppercase tracking-wider text-[#3B424B]">
                  <tr>
                    <th className="p-4 w-[42%]">Feature</th>
                    <th className="p-4">Solo Shop</th>
                    <th className="p-4 bg-[var(--tm-forest)]/5">Founding Dealer</th>
                    <th className="p-4">Multi-Location Pro</th>
                  </tr>
                </thead>
                <tbody className="text-[#0F1115]">
                  {[
                    { g: "Data & Uploads" },
                    ["Locations", "1", "Up to 3", "Unlimited"],
                    ["Active SKUs", "Up to 500", "Unlimited", "Unlimited"],
                    ["User seats", "1", "3", "Unlimited"],
                    ["Inventory + POS upload", true, true, true],
                    ["Column mapping templates", true, true, true],
                    ["Import Health Score", true, true, true],
                    { g: "Analytics" },
                    ["Reorder & stockout intelligence", true, true, true],
                    ["Slow-inventory report", true, true, true],
                    ["Pricing intelligence (margin $ & %)", "—", true, true],
                    ["Market comparison from feeds", "—", true, true],
                    ["Recommendations with evidence", true, true, true],
                    { g: "Ask TreadMargin (LLM)" },
                    ["Ask questions in plain English", true, true, true],
                    ["Question quota", "10 / day", "Unlimited", "Unlimited"],
                    ["Multi-turn conversations", true, true, true],
                    ["Pinned prompts", true, true, true],
                    ["Copy transcript + PDF export", "—", true, true],
                    { g: "Alerts" },
                    ["Weekly email digest", true, true, true],
                    ["Per-location digests", "—", "—", true],
                    ["Stock-out email alerts", true, true, true],
                    ["Stock-out SMS alerts (Twilio)", "—", "—", true],
                    { g: "Support" },
                    ["Email support", "Standard", "Priority", "Priority"],
                    ["Phone support", "—", "—", true],
                    ["Dedicated onboarding", "—", "—", true],
                    ["Monthly buying-strategy call", "—", "—", true],
                    { g: "Billing" },
                    ["Monthly price (CAD)", "$49", "$99", "$249"],
                    ["Yearly price (CAD, 2 mo free)", "$490", "$990", "$2,490"],
                    ["Cancel anytime", true, true, true],
                    ["Locked founding rate", "—", true, "—"],
                  ].map((row, i) => {
                    if (!Array.isArray(row) && row.g) {
                      return (
                        <tr key={`g-${i}`} className="border-t border-[var(--tm-border)] bg-[var(--tm-off)]/60">
                          <td colSpan={4} className="px-4 py-2.5 text-[11px] uppercase tracking-wider text-[#3B424B] font-semibold">{row.g}</td>
                        </tr>
                      );
                    }
                    const [label, s, f, m] = row;
                    const cell = (v) => {
                      if (v === true) return <CheckCircle2 size={16} className="text-[var(--tm-forest)]" />;
                      if (v === "—") return <span className="text-[#8b8f97]">—</span>;
                      return <span>{v}</span>;
                    };
                    return (
                      <tr key={label} className="border-t border-[var(--tm-border)]" data-testid={`compare-row-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}>
                        <td className="p-4 text-[#0F1115]">{label}</td>
                        <td className="p-4">{cell(s)}</td>
                        <td className="p-4 bg-[var(--tm-forest)]/5 font-medium">{cell(f)}</td>
                        <td className="p-4">{cell(m)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="tm-container py-20">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-wider text-[#3B424B] font-semibold mb-3">FAQ</div>
          <h2 className="text-3xl sm:text-4xl font-bold">Direct answers.</h2>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          {FAQ.map((f, i) => (
            <div key={i} className="tm-card p-6" data-testid={`faq-${i}`}>
              <div className="font-semibold">{f.q}</div>
              <div className="mt-2 text-sm text-[#3B424B] leading-relaxed">{f.a}</div>
            </div>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}
